// ABOUTME: The open positions under the applications block: saved searches and a filter row, the count and the order, one
// ABOUTME: card per listing, one more page on request, and the empty, off and error states. Off renders nothing at all.
import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { AppButton } from 'components/AppButton';
import { EmptyState } from 'components/EmptyState';
import { NotFoundErrorScreen } from 'components/screens';
import { Text } from 'components/Text';
import {
  sendAtsJobFeedFiltered,
  sendAtsJobFeedViewed
} from 'modules/analytics';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { readSessionJson, writeSessionJson } from 'utils/storage';
import { JOB_FEED_CRITERIA_KEY } from '../constants';
import {
  useCardSeen,
  useFilterOptions,
  useJobFeed,
  useListingFeedEnabled,
  useSearchPreferences
} from '../hooks';
import {
  JobListingSearchCriteria,
  JobListingSort,
  SavedSearch
} from '../types';
import {
  filterCount,
  INITIAL_FEED_CRITERIA,
  withoutUnservedCriteria
} from '../utils';
import { JobFeedFilters } from './JobFeedFilters';
import { JobFeedSkeleton } from './JobFeedSkeleton';
import { JobListingCard } from './JobListingCard';

const INITIAL = INITIAL_FEED_CRITERIA;

export const JobFeed = () => {
  // The criteria outlive the component: opening a job unmounts the feed, and a talent who set "remote only" must not
  // find it gone when they come back. Session storage, as a convenience; the default applies on a fresh session.
  const [criteria, setCriteria] = useState<JobListingSearchCriteria>(() =>
    withoutUnservedCriteria(
      readSessionJson<JobListingSearchCriteria>(JOB_FEED_CRITERIA_KEY, INITIAL)
    )
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const feed = useJobFeed(criteria);
  const { isEnabled: pjl } = useListingFeedEnabled();
  const { savedPoints, savedSearches, isLoaded, addPoint, saveSearch } =
    useSearchPreferences();
  const filterOptions = useFilterOptions();
  // PJL: each card counted SEEN for the operators the first time it is on screen.
  const seen = useCardSeen();
  const reported = useRef(false);

  const count = filterCount(criteria);
  const hasPoint = !!criteria.point;

  useEffect(() => {
    // One view per visit, guarded by a ref: a refetch or a filter change is not a new visit.
    if (feed.listings && !reported.current) {
      reported.current = true;
      sendAtsJobFeedViewed({
        filterCount: count,
        resultCount: feed.total,
        hasPoint
      });
    }
  }, [feed.listings, feed.total, count, hasPoint]);

  const apply = (applied: JobListingSearchCriteria) => {
    const next = withoutUnservedCriteria(applied);
    setCriteria(next);
    writeSessionJson(JOB_FEED_CRITERIA_KEY, next);
    sendAtsJobFeedFiltered({
      filterCount: filterCount(next),
      hasPoint: !!next.point
    });
  };

  const toggleSort = () => {
    if (!hasPoint) return;
    const next: JobListingSearchCriteria = {
      ...criteria,
      sort:
        criteria.sort === JobListingSort.DISTANCE
          ? JobListingSort.PUBLISHED_AT
          : JobListingSort.DISTANCE
    };
    setCriteria(next);
    writeSessionJson(JOB_FEED_CRITERIA_KEY, next);
  };

  /**
   * A saved search is posted back as it was stored; nothing in it is translated or filled in, only what pro-core does not
   * apply yet is left out (see withoutUnservedCriteria).
   */
  const replay = (search: SavedSearch) => apply(search.criteria);

  const invalidSearches = savedSearches.filter(
    search => search.valid === false
  );

  if (feed.isUnavailable) return null;
  if (feed.isLoading) return <JobFeedSkeleton />;
  if (feed.isError) {
    return (
      <NotFoundErrorScreen
        title={getTrans('jobs.error.title')}
        description1={getTrans('jobs.error.body1')}
        description2={getTrans('jobs.error.body2')}
      />
    );
  }
  if (!feed.listings) return null;

  const sortLabel =
    hasPoint && criteria.sort !== JobListingSort.PUBLISHED_AT
      ? getTrans('jobs.feed.sort.nearest')
      : getTrans('jobs.feed.sort.newest');

  return (
    <Box
      data-testid="job-feed"
      component="section"
      aria-label={getTrans('jobs.feed.title')}
    >
      {savedSearches.length > 0 && (
        <Box
          data-testid="job-feed-saved-searches"
          role="group"
          aria-label={getTrans('jobs.feed.savedSearches')}
          sx={{ mb: 1.5 }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {savedSearches.map(search => (
              <Chip
                key={search.refId ?? search.label}
                data-testid="job-feed-saved-search"
                clickable
                component="button"
                label={search.label}
                // pro-core keeps a search that no longer validates and says so; it is shown, greyed, never replayed.
                disabled={search.valid === false}
                onClick={() => replay(search)}
                variant="outlined"
                sx={{ height: 32 }}
              />
            ))}
          </Box>
          {invalidSearches.map(search => (
            <Text
              key={search.refId ?? search.label}
              variant="caption"
              color="text.secondary"
              component="p"
              data-testid="job-feed-saved-search-invalid"
              sx={{ mt: 0.5 }}
            >
              {getTransWithParams('jobs.feed.savedSearchInvalid', {
                label: search.label
              })}
            </Text>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          data-testid="job-feed-filters-chip"
          clickable
          component="button"
          onClick={() => setFiltersOpen(true)}
          label={
            count
              ? getTransWithParams('jobs.feed.filtersWithCount', { count })
              : getTrans('jobs.feed.filters')
          }
          color={count ? 'primary' : 'default'}
          variant={count ? 'filled' : 'outlined'}
          sx={{ height: 36, px: 0.5, fontWeight: 600 }}
        />
        <Chip
          clickable
          component="button"
          onClick={toggleSort}
          disabled={!hasPoint}
          label={getTrans('jobs.feed.sortBy')}
          variant="outlined"
          sx={{ height: 36, px: 0.5, fontWeight: 600 }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 1.5,
          px: 0.5
        }}
      >
        <Text
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: 0.4 }}
        >
          {getTransWithParams('jobs.feed.count', { count: feed.total })}
        </Text>
        <Text variant="caption" color="text.secondary">
          {sortLabel}
        </Text>
      </Box>

      {feed.listings.length === 0 ? (
        <EmptyState
          body1={getTrans('jobs.empty.body1')}
          body2={getTrans('jobs.empty.body2')}
        />
      ) : (
        feed.listings.map((card, index) => (
          <Box key={card.refId} ref={seen(card.refId)}>
            <JobListingCard card={card} position={index} />
          </Box>
        ))
      )}

      {feed.hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <AppButton
            variant="outlined"
            onClick={feed.loadMore}
            disabled={feed.isLoadingMore}
            testId="job-feed-load-more"
          >
            {getTrans('jobs.feed.loadMore')}
          </AppButton>
        </Box>
      )}

      <JobFeedFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        criteria={criteria}
        savedPoints={savedPoints}
        preferencesLoaded={isLoaded}
        filterOptions={filterOptions}
        filterOptionsLoaded={filterOptions.isLoaded}
        onApply={apply}
        onSavePoint={addPoint}
        onSaveSearch={saveSearch}
        pjl={pjl}
      />
    </Box>
  );
};
